import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Eye, ShieldOff, ShieldCheck, Download, MessageCircle, CheckCircle2, Circle, Bot } from 'lucide-react'
import { shopsApi, plansApi, type ShopsListParams } from '../../lib/api'
import DataTable, { Column } from '../../components/shared/DataTable'
import StatusBadge from '../../components/shared/StatusBadge'
import PlanBadge from '../../components/shared/PlanBadge'
import FilterBar from '../../components/shared/FilterBar'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import Pagination from '../../components/shared/Pagination'
import ErrorState from '../../components/shared/ErrorState'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { formatDate, formatRelativeTime } from '../../lib/utils'
import type { Shop } from '../../types'
import { tx } from '../../lib/i18n'

const statusOptions = [
  { label: tx('Active'), value: 'active' },
  { label: tx('Trial'), value: 'trial' },
  { label: tx('Suspended'), value: 'suspended' },
  { label: tx('Cancelled'), value: 'cancelled' },
]

const quickOptions = [
  { label: tx('Renewal within 14 days'), value: 'expiring' },
  { label: tx('No activity 30+ days'), value: 'inactive' },
]

const sortOptions = [
  { label: tx('Newest'), value: 'created' },
  { label: tx('Name'), value: 'name' },
  { label: tx('Renewal date'), value: 'nextBilling' },
  { label: tx('Last activity'), value: 'lastActivity' },
  { label: tx('Bookings (30 days)'), value: 'bookings30d' },
]

const daysUntil = (iso: string | null | undefined) =>
  iso ? Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000) : null

const AI_ADDON_NAMES: Record<string, string> = { ai_growth: 'AI Growth', ai_crm: 'AI CRM', ai_full: 'AI Full Suite' }

const ShopsPage: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [quickFilter, setQuickFilter] = useState('')
  const [sort, setSort] = useState('')
  const [exporting, setExporting] = useState(false)

  const { data: plans } = useQuery({
    queryKey: ['plans'],
    queryFn: () => plansApi.list().then((r) => r.data.data ?? []),
    staleTime: 5 * 60_000,
  })
  const planOptions = (plans ?? []).map((p) => ({ label: p.displayName || p.name, value: p.name }))

  const listParams: ShopsListParams = {
    search,
    status: statusFilter || undefined,
    plan: planFilter || undefined,
    expiringDays: quickFilter === 'expiring' ? 14 : undefined,
    inactiveDays: quickFilter === 'inactive' ? 30 : undefined,
    sort: (sort || undefined) as ShopsListParams['sort'],
  }

  const exportCsv = async () => {
    setExporting(true)
    try {
      await shopsApi.exportCsv(listParams)
    } catch {
      showToast({ type: 'error', message: tx('Export failed.') })
    } finally {
      setExporting(false)
    }
  }

  const [suspendDialog, setSuspendDialog] = useState<{ open: boolean; shop: Shop | null }>({ open: false, shop: null })
  const [reactivateDialog, setReactivateDialog] = useState<{ open: boolean; shop: Shop | null }>({ open: false, shop: null })
  const [reason, setReason] = useState('')

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['shops', { page, ...listParams }],
    queryFn: () => shopsApi.list({ page, limit: 20, ...listParams }).then((r) => r.data),
  })

  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => shopsApi.suspend(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops'] })
      showToast({ type: 'success', message: tx('Shop suspended successfully.') })
      setSuspendDialog({ open: false, shop: null })
      setReason('')
    },
    onError: () => showToast({ type: 'error', message: tx('Failed to suspend shop.') }),
  })

  const reactivateMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => shopsApi.reactivate(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops'] })
      showToast({ type: 'success', message: tx('Shop reactivated successfully.') })
      setReactivateDialog({ open: false, shop: null })
      setReason('')
    },
    onError: () => showToast({ type: 'error', message: tx('Failed to reactivate shop.') }),
  })

  const shops = data?.data ?? []
  const totalPages = data?.totalPages ?? 1
  const total = data?.total ?? 0

  const columns: Column<Shop>[] = [
    {
      key: 'name',
      header: tx('Shop'),
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900">{row.name}</p>
          <p className="text-xs text-gray-400">{row.businessTypeName ?? row.ownerPhone ?? '—'}</p>
        </div>
      ),
    },
    {
      key: 'ownerEmail',
      header: tx('Owner'),
      render: (row) => (
        <div className="min-w-0">
          <p className="text-gray-800 truncate">{row.ownerName ?? '—'}</p>
          <p className="text-xs text-gray-400 truncate">{row.ownerEmail || '—'}</p>
        </div>
      ),
    },
    {
      key: 'currentPlan',
      header: tx('Plan'),
      render: (row) => {
        const addons = row.subscription?.addons ?? []
        return (
          <span className="inline-flex items-center gap-1.5">
            <PlanBadge plan={row.currentPlan} />
            {addons.length > 0 && (
              <span className="relative group inline-flex">
                <span className="inline-flex items-center gap-0.5 rounded-full bg-purple-100 text-purple-700 px-1.5 py-0.5 text-[10px] font-semibold"><Bot className="w-3 h-3" /> AI</span>
                <span className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden min-w-[180px] rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg group-hover:block">
                  <div className="font-semibold mb-1">{tx('AI Add-ons')}</div>
                  {addons.map((a) => <div key={a}>• {AI_ADDON_NAMES[a] ?? a}</div>)}
                </span>
              </span>
            )}
          </span>
        )
      },
    },
    {
      key: 'nextBillingDate',
      header: tx('Renewal'),
      render: (row) => {
        const d = daysUntil(row.nextBillingDate)
        if (d === null) return <span className="text-gray-300 text-sm">—</span>
        const color = d < 0 ? 'text-red-600' : d <= 7 ? 'text-amber-600' : 'text-gray-600'
        return (
          <div>
            <p className="text-sm text-gray-700">{formatDate(row.nextBillingDate)}</p>
            <p className={`text-xs ${color}`}>
              {d < 0 ? `${tx('Overdue')} ${-d} ${tx('days')}` : `${tx('in')} ${d} ${tx('days')}`}
            </p>
          </div>
        )
      },
    },
    {
      key: 'activity',
      header: tx('Activity'),
      render: (row) => {
        const b = row.bookings30d ?? 0
        const idle = !row.lastActivityAt || Date.now() - new Date(row.lastActivityAt).getTime() > 30 * 86_400_000
        return (
          <div>
            <p className={`text-sm ${b === 0 ? 'text-red-600' : 'text-gray-700'}`}>
              {b} {tx('bookings / 30d')}
            </p>
            <p className={`text-xs ${idle ? 'text-red-500' : 'text-gray-400'}`}>
              {tx('Last active')} {formatRelativeTime(row.lastActivityAt)}
            </p>
          </div>
        )
      },
    },
    {
      key: 'setup',
      header: tx('Setup'),
      render: (row) => (
        <div className="flex items-center gap-2 text-xs">
          <span
            className={`inline-flex items-center gap-1 ${row.setup?.lineConnected ? 'text-green-600' : 'text-gray-300'}`}
            title={row.setup?.lineConnected ? tx('LINE OA connected') : tx('LINE OA not connected')}
          >
            <MessageCircle className="h-3.5 w-3.5" /> LINE
          </span>
          <span
            className={`inline-flex items-center gap-1 ${row.setup?.onboardingDone ? 'text-green-600' : 'text-gray-300'}`}
            title={row.setup?.onboardingDone ? tx('Onboarding done') : tx('Onboarding not finished')}
          >
            {row.setup?.onboardingDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
            {tx('Setup')}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: tx('Status'),
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'createdAt',
      header: tx('Created'),
      sortable: true,
      render: (row) => <span className="text-gray-500 text-sm">{formatDate(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: tx('Actions'),
      render: (row) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => navigate(`/shops/${row.id}`)}
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title={tx('View Details')}
          >
            <Eye className="h-4 w-4" />
          </button>
          {can('shops.suspend') && (row.status === 'suspended' ? (
            <button
              onClick={() => { setReactivateDialog({ open: true, shop: row }); setReason('') }}
              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title={tx('Reactivate')}
            >
              <ShieldCheck className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={() => { setSuspendDialog({ open: true, shop: row }); setReason('') }}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title={tx('Suspend')}
            >
              <ShieldOff className="h-4 w-4" />
            </button>
          ))}
        </div>
      ),
    },
  ]

  if (error) return <ErrorState message={tx('Failed to load shops.')} onRetry={() => refetch()} />

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{tx('Shops')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{tx('Manage all registered beauty & wellness shops')}</p>
        </div>
        {can('shops.export') && (
        <button
          onClick={exportCsv}
          disabled={exporting}
          data-testid="shops-export-csv"
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className="h-4 w-4" /> {exporting ? tx('Exporting…') : tx('Export CSV')}
        </button>
        )}
      </div>

      <FilterBar
        search={{ value: search, onChange: (v) => { setSearch(v); setPage(1) }, placeholder: tx('Search by name, email, phone...') }}
        filters={[
          {
            key: 'status',
            label: tx('Status'),
            options: statusOptions,
            value: statusFilter,
            onChange: (v) => { setStatusFilter(v); setPage(1) },
          },
          {
            key: 'plan',
            label: tx('Plan'),
            options: planOptions,
            value: planFilter,
            onChange: (v) => { setPlanFilter(v); setPage(1) },
          },
          {
            key: 'quick',
            label: tx('Watch list'),
            options: quickOptions,
            value: quickFilter,
            onChange: (v) => { setQuickFilter(v); setPage(1) },
          },
          {
            key: 'sort',
            label: tx('Sort'),
            options: sortOptions.slice(1),
            value: sort,
            onChange: setSort,
          },
        ]}
        onReset={() => { setSearch(''); setStatusFilter(''); setPlanFilter(''); setQuickFilter(''); setSort(''); setPage(1) }}
      />

      <div>
        <DataTable
          columns={columns}
          data={shops}
          isLoading={isLoading}
          emptyMessage={tx('No shops found.')}
          onRowClick={(row) => navigate(`/shops/${row.id}`)}
        />
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          limit={20}
          onPageChange={setPage}
        />
      </div>

      <ConfirmDialog
        open={suspendDialog.open}
        onClose={() => setSuspendDialog({ open: false, shop: null })}
        onConfirm={() => {
          if (suspendDialog.shop) suspendMutation.mutate({ id: suspendDialog.shop.id, reason })
        }}
        title={`${tx('Suspend')} "${suspendDialog.shop?.name}"`}
        description={tx('This will prevent the shop owner from accessing the platform. You must provide a reason.')}
        confirmLabel={tx('Suspend Shop')}
        variant="danger"
        requireReason
        reason={reason}
        onReasonChange={setReason}
        isLoading={suspendMutation.isPending}
      />

      <ConfirmDialog
        open={reactivateDialog.open}
        onClose={() => setReactivateDialog({ open: false, shop: null })}
        onConfirm={() => {
          if (reactivateDialog.shop) reactivateMutation.mutate({ id: reactivateDialog.shop.id, reason })
        }}
        title={`${tx('Reactivate')} "${reactivateDialog.shop?.name}"`}
        description={tx("This will restore the shop's access to the platform.")}
        confirmLabel={tx('Reactivate')}
        variant="default"
        requireReason
        reason={reason}
        onReasonChange={setReason}
        isLoading={reactivateMutation.isPending}
      />
    </div>
  )
}

export default ShopsPage
